import { html, render } from 'lit-html';

import prevIcon from '@disy/cadenza-icons/chevron-left.svg';
import nextIcon from '@disy/cadenza-icons/chevron-right.svg';
import { createIconButton } from '@disy/ui/button';
import type { Icon } from '@disy/ui/icon';
import { icon } from '@disy/ui/icon';

import { paginationI18n } from './pagination-i18n';

export class ChangePageEvent extends CustomEvent<{
  /* Integer page change delta from current page */
  delta: number;
}> {
  static get type() {
    return 'changePage' as const;
  }
  constructor(delta: number) {
    super(ChangePageEvent.type, { bubbles: true, detail: { delta } });
  }
}

declare global {
  interface HTMLElementEventMap {
    [ChangePageEvent.type]: ChangePageEvent;
  }
}

const COMPONENT_NAME = 'd-pagination-control-stepper';
const DEFAULT_CURRENT_PAGE = 1;
const DEFAULT_PAGE_COUNT = 1;

export class PaginationControlStepper extends HTMLElement {
  #currentPage: number = DEFAULT_CURRENT_PAGE;
  #pageCount: number = DEFAULT_PAGE_COUNT;

  static get observedAttributes() {
    return ['current-page', 'page-count'];
  }

  attributeChangedCallback() {
    this.#currentPage = getDefaultedNumericAttributeValue(this, 'current-page', DEFAULT_CURRENT_PAGE);
    this.#pageCount = getDefaultedNumericAttributeValue(this, 'page-count', DEFAULT_CURRENT_PAGE);
    this.#render();
  }

  connectedCallback() {
    this.#render();
  }

  #render() {
    this.classList.add(COMPONENT_NAME);
    this.classList.add('d-stack-h');
    this.classList.add('space-1');
    const previousButton = this._createPaginationButton(
      icon(prevIcon),
      paginationI18n(this, 'prev'),
      -1,
      this.#currentPage === 1,
    );
    const nextButton = this._createPaginationButton(
      icon(nextIcon),
      paginationI18n(this, 'next'),
      +1,
      this.#currentPage === this.#pageCount,
    );

    render(html` ${previousButton} ${nextButton} `, this);
  }

  _loadPage(delta: number) {
    this.setAttribute('current-page', (this.#currentPage + delta).toString());
    this.dispatchEvent(new ChangePageEvent(delta));
  }

  _createPaginationButton(iconElement: Icon, label: string, delta: number, disabled: boolean) {
    return createIconButton(iconElement, label, {
      attrs: { 'aria-controls': this.getAttribute('aria-controls') ?? this.id },
      disabled,
      onclick: () => this._loadPage(delta),
      size: 's',
      variant: 'borderless',
    });
  }
}

customElements.define(COMPONENT_NAME, PaginationControlStepper);
declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_NAME]: PaginationControlStepper;
  }
}

function getDefaultedNumericAttributeValue(element: Element, attributeName: string, defaultValue: number) {
  return element.hasAttribute(attributeName) ? Number.parseInt(element.getAttribute(attributeName)!) : defaultValue;
}
